import { IDocumentFileModel, IDocumentFolderModel, IExternalLinkModel, IUser } from '@api/models';
import { TRootState } from '@store/index';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

type DocumentTree = Record<
    string,
    {
        folders: IDocumentFolderWithAccess[];
        files: IDocumentFileModel[];
        externalLinks: IExternalLinkModel[];
    }
>;

export type IDocumentFolderWithAccess = IDocumentFolderModel & {
    folderSize: number;
    fileUsers: IUser[];
    externalLinkUsers: IUser[];
    fileCount: number;
    externalLinkCount: number;
};

interface DocumentHierarchyOptions {
    folders: IDocumentFolderModel[];
    files: IDocumentFileModel[];
    externalLinks: IExternalLinkModel[];
}
const useDocumentHierarchyWithoutFilter = (options: DocumentHierarchyOptions) => {
    const { folders, files, externalLinks } = options;

    const { info: user } = useSelector((state: TRootState) => state.profile);

    const foldersWithAccess = useMemo(() => {
        // Create a map for quick folder lookup
        const folderMap = new Map<string, IDocumentFolderModel>(folders.map((f) => [f._id, { ...f }]));
        const folderWithAccessMap = new Map<string, IDocumentFolderWithAccess>();

        // Recursive function to calculate folder stats including all nested content
        const calculateFolderStats = (folderId: string): IDocumentFolderWithAccess | null => {
            // Return cached result if already calculated
            if (folderWithAccessMap.has(folderId)) {
                return folderWithAccessMap.get(folderId);
            }

            const folder = folderMap.get(folderId);

            // Get direct children
            const directFiles = files.filter((row) => row.folder === folderId);
            const directLinks = externalLinks.filter((row) => row.folder === folderId);
            const childFolders = folders.filter((row) => row.parentId === folderId);

            // Calculate direct file size
            let totalSize = directFiles.reduce((total, file) => {
                const sizeInBytes = parseInt(file.fileSize) || 0;
                return total + sizeInBytes;
            }, 0);

            // Collect direct users
            let allFileUsers: IUser[] = directFiles.flatMap((file) => file.users as IUser[]);
            let allLinkUsers: IUser[] = directLinks.flatMap((link) => link.users as IUser[]);

            // Recursively add from child folders
            let totalFileCount = directFiles.length;
            let totalLinkCount = directLinks.length;

            childFolders.forEach((childFolder) => {
                const childStats = calculateFolderStats(childFolder._id);

                if (childStats) {
                    totalSize += childStats.folderSize;
                    allFileUsers = [...allFileUsers, ...childStats.fileUsers];
                    allLinkUsers = [...allLinkUsers, ...childStats.externalLinkUsers];
                    totalFileCount += childStats.fileCount;
                    totalLinkCount += childStats.externalLinkCount;
                }
            });

            // Deduplicate users by _id
            const uniqueFileUsers = Array.from(new Map(allFileUsers.map((u) => [u._id, u])).values());

            const uniqueLinkUsers = Array.from(new Map(allLinkUsers.map((u) => [u._id, u])).values());

            const result = {
                ...folder,
                folderSize: totalSize,
                fileUsers: uniqueFileUsers,
                externalLinkUsers: uniqueLinkUsers,
                fileCount: totalFileCount,
                externalLinkCount: totalLinkCount,
            };

            folderWithAccessMap.set(folderId, result);

            return result;
        };

        // Calculate stats for all folders
        const allFoldersWithStats = folders.map((folder) => calculateFolderStats(folder._id));
        return allFoldersWithStats;

        // Filter folders based on user access
        // return allFoldersWithStats.filter((folder) => {
        //     // Check if user created it
        //     const isCreator = (folder.createdBy as IUser)._id === user._id;

        //     // Check if user has access to files in this folder (including nested)
        //     const hasFileAccess = folder.fileUsers.some((u) => u._id === user._id);

        //     // 3. User has access to external links in this folder (including nested)
        //     const hasLinkAccess = folder.externalLinkUsers.some((u) => u._id === user._id);

        //     return isCreator || hasFileAccess || hasLinkAccess;
        // });
    }, [folders, files, externalLinks, user]);

    const documentTreeMap = useMemo(() => {
        const documentTree = {} as DocumentTree;

        documentTree['root'] = {
            folders: foldersWithAccess.filter((row) => !row.parentId),
            files: files.filter((row) => !row.folder),
            externalLinks: externalLinks.filter((row) => !row.folder),
        };

        foldersWithAccess.forEach((folder) => {
            documentTree[folder._id] = {
                folders: foldersWithAccess.filter((row) => row.parentId === folder._id),
                files: files.filter((row) => row.folder === folder._id),
                externalLinks: externalLinks.filter((row) => row.folder === folder._id),
            };
        });

        return documentTree;
    }, [externalLinks, files, foldersWithAccess]);

    const parentTreeMap = useMemo(() => {
        const parentTree = {} as Record<string, IDocumentFolderModel>;

        folders.forEach((folder) => {
            parentTree[folder._id] = folders.find((row) => row._id === folder.parentId) || null;
        });

        return parentTree;
    }, [folders]);

    const getChildren = useCallback(
        (folderId: string) => {
            return documentTreeMap[folderId];
        },
        [documentTreeMap]
    );

    const getParentFolder = useCallback(
        (folderId: string) => {
            return parentTreeMap[folderId] || null;
        },
        [parentTreeMap]
    );

    const getFolderBreadCrumb = useCallback(
        (folderId: string) => {
            const items: { name: string; folderId: string }[] = [];

            if (!folderId) return items;

            const folder = folders.find((r) => r._id === folderId);

            if (!folder) return items;

            items.push({
                name: folder.name,
                folderId: folder._id,
            });

            let parent = getParentFolder(folder._id);

            while (parent) {
                items.push({
                    name: parent.name,
                    folderId: parent._id,
                });

                parent = getParentFolder(parent._id);
            }

            items.push();

            return items.reverse();
        },
        [folders, getParentFolder]
    );

    return {
        getChildren,
        getFolderBreadCrumb,
    };
};

export default useDocumentHierarchyWithoutFilter;
